import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { TemplateSection } from '../../types';

export interface ParameterItem {
  api_source: string;
  api_source_id?: number;
  field_path: string;
  display_name: string;
}

interface ParameterPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  availableApis: Array<{ id: number; name: string; description?: string }>;
  apiDisplayParams: Record<string, Record<string, string>>;
  availableFields?: Record<string, string[]>;
  sections: TemplateSection[];
  initialTargetSectionId?: string;
  onAddFields: (
    targetSectionId: string | 'NEW_SECTION',
    fields: ParameterItem[],
    newSectionHeading?: string
  ) => void;
}

export const ParameterPickerModal: React.FC<ParameterPickerModalProps> = ({
  isOpen,
  onClose,
  availableApis,
  apiDisplayParams,
  availableFields = {},
  sections,
  initialTargetSectionId,
  onAddFields,
}) => {
  const [selectedApiFilter, setSelectedApiFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [hideAlreadyAdded, setHideAlreadyAdded] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [targetSectionId, setTargetSectionId] = useState<string>(
    initialTargetSectionId || (sections[0]?.id ?? 'NEW_SECTION')
  );
  const [newSectionHeading, setNewSectionHeading] = useState<string>('');

  useEffect(() => {
    if (initialTargetSectionId) {
      setTargetSectionId(initialTargetSectionId);
    } else if (sections.length > 0) {
      setTargetSectionId(sections[0].id || 'NEW_SECTION');
    } else {
      setTargetSectionId('NEW_SECTION');
    }
  }, [initialTargetSectionId, sections, isOpen]);

  // Reset selection when modal closes or opens
  useEffect(() => {
    if (isOpen) {
      setSelectedKeys(new Set());
      setSearchQuery('');
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Compile all available parameters across APIs
  const allParameters = useMemo(() => {
    const list: ParameterItem[] = [];
    const seen = new Set<string>();

    availableApis.forEach((api) => {
      const displayParams = apiDisplayParams[api.name] || {};
      const fieldsList = availableFields[api.name] || [];

      // 1. Add fields with configured display names
      Object.entries(displayParams).forEach(([path, displayName]) => {
        if (typeof displayName === 'string' && displayName.trim().length > 0) {
          const key = `${api.name}::${path}`;
          if (!seen.has(key)) {
            seen.add(key);
            list.push({
              api_source: api.name,
              api_source_id: api.id,
              field_path: path,
              display_name: displayName.trim(),
            });
          }
        }
      });

      // 2. Add any additional fields discovered from responses
      fieldsList.forEach((path) => {
        const key = `${api.name}::${path}`;
        if (!seen.has(key)) {
          seen.add(key);
          // Humanize path as fallback display name
          const humanized = path
            .replace(/[._]/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
          list.push({
            api_source: api.name,
            api_source_id: api.id,
            field_path: path,
            display_name: humanized,
          });
        }
      });
    });

    return list;
  }, [availableApis, apiDisplayParams, availableFields]);

  // Check if a parameter is already added in the template
  const existingFieldKeys = useMemo(() => {
    const keys = new Set<string>();
    sections.forEach((section) => {
      section.fields.forEach((f) => {
        keys.add(`${f.api_source}::${f.field_path}`);
      });
    });
    return keys;
  }, [sections]);

  // Filter parameters by API, search query, and hideAlreadyAdded
  const filteredParameters = useMemo(() => {
    return allParameters.filter((param) => {
      const uniqueKey = `${param.api_source}::${param.field_path}`;
      if (hideAlreadyAdded && existingFieldKeys.has(uniqueKey)) {
        return false;
      }
      if (selectedApiFilter !== 'ALL' && param.api_source !== selectedApiFilter) {
        return false;
      }
      if (!searchQuery.trim()) return true;

      const q = searchQuery.toLowerCase();
      return (
        param.display_name.toLowerCase().includes(q) ||
        param.field_path.toLowerCase().includes(q) ||
        param.api_source.toLowerCase().includes(q)
      );
    });
  }, [allParameters, selectedApiFilter, searchQuery, hideAlreadyAdded, existingFieldKeys]);

  const toggleSelectKey = (uniqueKey: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(uniqueKey)) {
        next.delete(uniqueKey);
      } else {
        next.add(uniqueKey);
      }
      return next;
    });
  };

  const handleSelectAllFiltered = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      filteredParameters.forEach((param) => {
        next.add(`${param.api_source}::${param.field_path}`);
      });
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedKeys(new Set());
  };

  const handleAddSelected = () => {
    const selectedItems = allParameters.filter((param) =>
      selectedKeys.has(`${param.api_source}::${param.field_path}`)
    );

    if (selectedItems.length === 0) return;

    onAddFields(targetSectionId, selectedItems, newSectionHeading.trim() || undefined);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-4xl flex flex-col max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Select API Response Parameters
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Choose parameters from integrated providers and assign them to a report section.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filter Toolbar */}
        <div className="p-5 border-b border-gray-200 dark:border-gray-700 space-y-3 bg-white dark:bg-gray-800 shrink-0">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
            {/* Search Input with Clear Button */}
            <div className="relative flex-1">
              <svg
                className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search parameter by name or path (e.g., name, phone, carrier)..."
                className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-0.5 rounded-full"
                  title="Clear search"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Quick Select Buttons & Hide Added Toggle */}
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <label className="flex items-center gap-2 cursor-pointer select-none text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                <input
                  type="checkbox"
                  checked={hideAlreadyAdded}
                  onChange={(e) => setHideAlreadyAdded(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-3.5 h-3.5 cursor-pointer"
                />
                <span>Hide already added</span>
                {existingFieldKeys.size > 0 && (
                  <span className="text-[10px] bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-1.5 py-0.2 rounded-full">
                    {existingFieldKeys.size}
                  </span>
                )}
              </label>
              <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 hidden sm:block" />
              <button
                type="button"
                onClick={handleSelectAllFiltered}
                className="px-2.5 py-1.5 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
              >
                Select All ({filteredParameters.length})
              </button>
              <button
                type="button"
                onClick={handleClearSelection}
                disabled={selectedKeys.size === 0}
                className="px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-40"
              >
                Clear ({selectedKeys.size})
              </button>
            </div>
          </div>

          {/* API Filter Pills */}
          <div className="flex flex-wrap gap-1.5 items-center pt-1">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-1">Provider:</span>
            <button
              type="button"
              onClick={() => setSelectedApiFilter('ALL')}
              className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                selectedApiFilter === 'ALL'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All Providers ({allParameters.length})
            </button>
            {availableApis.map((api) => {
              const count = allParameters.filter((p) => p.api_source === api.name).length;
              return (
                <button
                  key={api.id}
                  type="button"
                  onClick={() => setSelectedApiFilter(api.name)}
                  className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
                    selectedApiFilter === api.name
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {api.name} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {/* Parameters Grid / List */}
        <div className="flex-1 overflow-y-auto p-5 space-y-2 bg-gray-50/50 dark:bg-gray-900/40">
          {filteredParameters.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                No parameters found
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {searchQuery
                  ? `No parameters matched "${searchQuery}". Try a different search.`
                  : 'No display parameters found for the selected API provider.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {filteredParameters.map((param) => {
                const uniqueKey = `${param.api_source}::${param.field_path}`;
                const isSelected = selectedKeys.has(uniqueKey);
                const isAlreadyInTemplate = existingFieldKeys.has(uniqueKey);

                return (
                  <div
                    key={uniqueKey}
                    onClick={() => toggleSelectKey(uniqueKey)}
                    className={`flex items-start gap-3 p-3 rounded-xl border transition-all cursor-pointer select-none ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-950/40 border-indigo-300 dark:border-indigo-600 ring-1 ring-indigo-400'
                        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelectKey(uniqueKey)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 w-4 h-4 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="font-semibold text-sm text-gray-900 dark:text-white truncate">
                          {param.display_name}
                        </span>
                        {isAlreadyInTemplate && (
                          <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded shrink-0">
                            Already Added
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 font-mono truncate">
                        <Badge variant="outline" size="sm" className="text-[10px] py-0 px-1 font-sans">
                          {param.api_source}
                        </Badge>
                        <span className="truncate" title={param.field_path}>
                          {param.field_path}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer / Action Bar */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 shrink-0">
          {/* Target Section Selector */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-1">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 shrink-0">
              Add to Section:
            </span>
            <select
              value={targetSectionId}
              onChange={(e) => setTargetSectionId(e.target.value)}
              className="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full sm:w-auto min-w-[180px]"
            >
              {sections.map((sec, idx) => (
                <option key={sec.id || idx} value={sec.id || `section_${idx}`}>
                  {sec.heading} ({sec.fields.length} fields)
                </option>
              ))}
              <option value="NEW_SECTION">+ Create New Section...</option>
            </select>

            {targetSectionId === 'NEW_SECTION' && (
              <input
                type="text"
                value={newSectionHeading}
                onChange={(e) => setNewSectionHeading(e.target.value)}
                placeholder="New Section Name (e.g., Identity Info)"
                className="text-xs border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full sm:w-auto"
              />
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={selectedKeys.size === 0 || (targetSectionId === 'NEW_SECTION' && !newSectionHeading.trim())}
              onClick={handleAddSelected}
              className="gap-1.5 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add {selectedKeys.size > 0 ? `(${selectedKeys.size})` : ''} to Section
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
